// Product-line classification for the Inventory page filter.
//
// SKU categories carry NO product-line column — the rest of the app already
// identifies the insulation side by category NAME (seed-catalog.sql JOINs on
// c.name = 'Insulation' / 'Safety/PPE'). We mirror that here.
//
// This is a COMPLEMENT classifier: gutter = "everything that isn't insulation".
// That's fall-through-safe (no unhandled value), but its default direction is
// SILENT — any NEW insulation-side category (e.g. "Insulation Accessories")
// lands under Gutter until it's added to the set below. The sibling test pins
// the known seed names so such drift trips a red test instead of mis-bucketing
// in prod. Single-tenant prod, so name-matching is acceptable.
export type ProductLine = 'gutter' | 'insulation';

export const INSULATION_CATEGORY_NAMES = [
  'Insulation',
  'Insulation Accessories',
  'Safety/PPE',
];

const INSULATION_SET = new Set(
  INSULATION_CATEGORY_NAMES.map((n) => n.toLowerCase()),
);

export function isInsulationCategory(name: string): boolean {
  return INSULATION_SET.has(name.trim().toLowerCase());
}

// Within the Insulation product line, only `Insulation` is TRUE BATTING (carries
// r_value / coverage_sf / width / length). `Insulation Accessories` + `Safety/PPE`
// are insulation-line but NOT batting → they render with the standard columns
// (Name etc.), not the batting attribute columns.
//
// SILENT-DEFAULT RISK: a future batting category name (e.g. 'Blown-In') would fall
// to the non-batting bucket until added here — but that default is the SAFE one
// (it shows the standard columns with the name VISIBLE, never the hidden-name bug
// this split fixes). The sibling test pins the known name so a code-side drift
// trips red. INVARIANT: batting ⊂ insulation (every batting name is insulation).
export const BATTING_CATEGORY_NAMES = ['Insulation'];

const BATTING_SET = new Set(BATTING_CATEGORY_NAMES.map((n) => n.toLowerCase()));

export function isBattingCategory(name: string): boolean {
  return BATTING_SET.has(name.trim().toLowerCase());
}

// Within the GUTTER product line, the `Gutter Coil` category carries the 6
// extracted coil attributes (Size/Style/Color/Width/Thickness/Material) and
// renders its own attribute columns — every other gutter category uses the
// standard Name column. Name-based, mirroring isBattingCategory. After the
// half-round recat, half-round coils live here too.
export const GUTTER_COIL_CATEGORY_NAMES = ['Gutter Coil'];

const GUTTER_COIL_SET = new Set(GUTTER_COIL_CATEGORY_NAMES.map((n) => n.toLowerCase()));

export function isGutterCoilCategory(name: string): boolean {
  return GUTTER_COIL_SET.has(name.trim().toLowerCase());
}

// Within the GUTTER product line, the `Downspouts` category carries 5 extracted
// downspout attributes (Style/Connector Size/Length/Material/Color) and renders
// its own attribute columns. Mirrors isGutterCoilCategory. NOTE: the separate
// `Elbows` category is intentionally NOT included (different part, not requested).
export const DOWNSPOUT_CATEGORY_NAMES = ['Downspouts'];

const DOWNSPOUT_SET = new Set(DOWNSPOUT_CATEGORY_NAMES.map((n) => n.toLowerCase()));

export function isDownspoutCategory(name: string): boolean {
  return DOWNSPOUT_SET.has(name.trim().toLowerCase());
}

// Within the GUTTER product line, the `Elbows` category carries 6 extracted elbow
// attributes (Style/Connector Size/Type/Angle/Material/Color) and renders its own
// attribute columns. Mirrors isDownspoutCategory.
export const ELBOW_CATEGORY_NAMES = ['Elbows'];

const ELBOW_SET = new Set(ELBOW_CATEGORY_NAMES.map((n) => n.toLowerCase()));

export function isElbowCategory(name: string): boolean {
  return ELBOW_SET.has(name.trim().toLowerCase());
}

// Within the GUTTER product line, the `Miters` category carries 6 extracted miter
// attributes (Style/Type/Position/Size/Material/Color). Mirrors isElbowCategory.
// (Created by the Miters/End-Caps recat; End Caps is its own category — standard
// columns for now, its own attribute table later.)
export const MITER_CATEGORY_NAMES = ['Miters'];

const MITER_SET = new Set(MITER_CATEGORY_NAMES.map((n) => n.toLowerCase()));

export function isMiterCategory(name: string): boolean {
  return MITER_SET.has(name.trim().toLowerCase());
}

// Within the GUTTER product line, the `End Caps` category carries 5 extracted
// end-cap attributes (Style/Size/Side L-R/Material/Color). Mirrors isMiterCategory.
// (Created by the Miters/End-Caps recat.)
export const ENDCAP_CATEGORY_NAMES = ['End Caps'];

const ENDCAP_SET = new Set(ENDCAP_CATEGORY_NAMES.map((n) => n.toLowerCase()));

export function isEndCapCategory(name: string): boolean {
  return ENDCAP_SET.has(name.trim().toLowerCase());
}

// Within the GUTTER product line, the `Gutter Guards` category carries 6 extracted
// guard attributes (Brand/Type/Width/Length/Material/Color). Mirrors isEndCapCategory.
// (Step 0 confirmed the dev category name; add any prod-divergent name here too.)
export const GUTTER_GUARD_CATEGORY_NAMES = ['Gutter Guards'];

const GUTTER_GUARD_SET = new Set(GUTTER_GUARD_CATEGORY_NAMES.map((n) => n.toLowerCase()));

export function isGutterGuardCategory(name: string): boolean {
  return GUTTER_GUARD_SET.has(name.trim().toLowerCase());
}

// Within the GUTTER product line, `Outlets` (4 attrs: Shape/Size/Material/Color)
// and `Adapters & Conductors` (6 attrs: Type/Style/Dim1/Dim2/Material/Color) each
// carry their own attribute columns. Created by the 2026-06-17 Outlets & Connectors
// recat. ("Accessories" + "Miscellaneous" from that recat are standard-column
// catch-alls — no attribute table, so no helper.)
export const OUTLET_CATEGORY_NAMES = ['Outlets'];
const OUTLET_SET = new Set(OUTLET_CATEGORY_NAMES.map((n) => n.toLowerCase()));
export function isOutletCategory(name: string): boolean {
  return OUTLET_SET.has(name.trim().toLowerCase());
}

export const ADAPTER_CONDUCTOR_CATEGORY_NAMES = ['Adapters & Conductors'];
const ADAPTER_CONDUCTOR_SET = new Set(ADAPTER_CONDUCTOR_CATEGORY_NAMES.map((n) => n.toLowerCase()));
export function isAdapterConductorCategory(name: string): boolean {
  return ADAPTER_CONDUCTOR_SET.has(name.trim().toLowerCase());
}

export const HANGER_CATEGORY_NAMES = ['Hangers & Pipe Bands'];
const HANGER_SET = new Set(HANGER_CATEGORY_NAMES.map((n) => n.toLowerCase()));
export function isHangerCategory(name: string): boolean {
  return HANGER_SET.has(name.trim().toLowerCase());
}

export const WEDGE_SHANK_CATEGORY_NAMES = ['Wedges & Shanks'];
const WEDGE_SHANK_SET = new Set(WEDGE_SHANK_CATEGORY_NAMES.map((n) => n.toLowerCase()));
export function isWedgeShankCategory(name: string): boolean {
  return WEDGE_SHANK_SET.has(name.trim().toLowerCase());
}

// Master-data rework (2026-06-19): the legacy single "Screws Bolts Fasteners"
// category was split into Fasteners / Staples / Bolts & Anchors. All three share
// the same attribute axes (Type/Gauge/Length/Color) → all map to the fastener set.
export const FASTENER_CATEGORY_NAMES = ['Fasteners', 'Staples', 'Bolts & Anchors'];
const FASTENER_SET = new Set(FASTENER_CATEGORY_NAMES.map((n) => n.toLowerCase()));
export function isFastenerCategory(name: string): boolean {
  return FASTENER_SET.has(name.trim().toLowerCase());
}

export const SEALER_PAINT_CATEGORY_NAMES = ['Sealer & Paint'];
const SEALER_PAINT_SET = new Set(SEALER_PAINT_CATEGORY_NAMES.map((n) => n.toLowerCase()));
export function isSealerPaintCategory(name: string): boolean {
  return SEALER_PAINT_SET.has(name.trim().toLowerCase());
}

export const TOOL_CATEGORY_NAMES = ['Tools & Equipment'];
const TOOL_SET = new Set(TOOL_CATEGORY_NAMES.map((n) => n.toLowerCase()));
export function isToolCategory(name: string): boolean {
  return TOOL_SET.has(name.trim().toLowerCase());
}

export function categoryMatchesProductLine(
  name: string,
  line: ProductLine,
): boolean {
  return line === 'insulation'
    ? isInsulationCategory(name)
    : !isInsulationCategory(name);
}
